import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CommonTable from "../../../../common/table/ComonTable";
import DeleteModal from "../../../../common/commonmodal/DeleteModal";
import CommonModal from "../../../../common/commonmodal/CommonModal";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faUser, faEye, faFileAlt, faExternalLinkAlt, faTrash } from "@fortawesome/free-solid-svg-icons";
import apiClient from "../../../../../services/apiClient";
import staffService from "../../../../../services/staffService";
import storageService from "../../../../../utils/storageService";

const UNIT_LIST_URL = "/UnitMaster/GetAllUnitMaster";

const StaffList = ({ listMode = "unit" }) => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const assignedSiteId = user?.siteId || user?.SiteId || Number(storageService.getItem("siteId")) || 2;

    const [staffList, setStaffList] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState("");
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0
    });

    const userRoleId = Number(storageService.getItem("roleId") || user?.roleId || 0);
    const personTypeId = Number(storageService.getItem("personTypeId") || user?.personTypeId || user?.PersonTypeId || 0);
    const loggedInUnitId = Number(user?.branchorunitID || user?.branchOrUnitId || 0);
    const isVendorAdmin = userRoleId === 7 || personTypeId === 4;
    const isAllStaff = listMode === "all";
    const rawVendorId = isVendorAdmin
        ? (user?.id || user?.Id || user?.userId || storageService.getItem("employeeCode"))
        : null;
    const vendorId = rawVendorId != null && Number(rawVendorId) > 0 ? Number(rawVendorId) : null;

    const [filterUnitId, setFilterUnitId] = useState("");
    const [filterVendorId, setFilterVendorId] = useState("");
    const [units, setUnits] = useState([]);
    const [vendors, setVendors] = useState([]);

    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        staffId: null,
        staffName: ""
    });

    const [previewDoc, setPreviewDoc] = useState({
        isOpen: false,
        doc: null,
        staffName: ""
    });
    const [staffLogins, setStaffLogins] = useState([]);
    const [loginModal, setLoginModal] = useState({ isOpen: false, staff: null, staffRoleId: "", password: "" });
    const [credentialModal, setCredentialModal] = useState({ isOpen: false, userName: "", temporaryPassword: "" });
    const actorName = user?.name || user?.Name || user?.userName || storageService.getItem("employeeName") || "Vendor";

    const fetchStaff = useCallback(async (customParams = {}) => {
        setLoading(true);
        try {
            const params = {
                siteId: assignedSiteId,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
                ...customParams
            };

            if (isAllStaff) {
                if (filterUnitId) params.unitId = Number(filterUnitId);
                if (filterVendorId) params.vendorId = Number(filterVendorId);
            } else if (isVendorAdmin && vendorId) {
                params.vendorId = Number(vendorId);
                if (selectedRole) params.staffRoleId = Number(selectedRole);
            } else if (loggedInUnitId > 0) {
                params.unitId = loggedInUnitId;
            }

            const cleanParams = Object.fromEntries(
                Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== "")
            );

            const response = await apiClient.get('/staff-management/staff/GetStaff', {
                params: cleanParams
            });

            if (response.data?.isSuccess && response.data?.data) {
                const list = response.data.data.map(item => {
                    const primaryDoc = Array.isArray(item.documents) && item.documents.length > 0 ? item.documents[0] : null;
                    const primaryAllotment = Array.isArray(item.allotments) && item.allotments.length > 0 ? item.allotments[0] : null;
                    
                    return {
                        ...item,
                        id: item.staffId,
                        fullName: `${item.firstName || ''} ${item.lastName || ''}`.trim() || "—",
                        document: primaryDoc,
                        allotment: primaryAllotment
                    };
                });
                
                setStaffList(list);
                setPagination(prev => ({
                    ...prev,
                    totalRecords: Number(response.data.totalCount) || list.length
                }));
            } else {
                setStaffList([]);
                setPagination(prev => ({ ...prev, totalRecords: 0 }));
            }
        } catch (error) {
            console.error("Failed to fetch staff list:", error);
            toast.error("Failed to load staff list");
            setStaffList([]);
        } finally {
            setLoading(false);
        }
    }, [assignedSiteId, pagination.pageNumber, pagination.pageSize, isVendorAdmin, vendorId, selectedRole, isAllStaff, filterUnitId, filterVendorId, loggedInUnitId]);

    const fetchRoles = useCallback(async () => {
        try {
            let categoryId = null;
            if (isVendorAdmin && vendorId) {
                const vendorResponse = await apiClient.get('/vendor-management/vendor/GetVendors', {
                    params: { siteId: assignedSiteId, vendorId: Number(vendorId), pageNumber: 1, pageSize: 1 }
                });
                categoryId = vendorResponse.data?.data?.[0]?.categoryId || null;
            }
            const res = await apiClient.get('/staff-management/staff/GetRoles', {
                params: { siteId: assignedSiteId, ...(categoryId ? { categoryId } : {}) }
            });
            if (res.data?.isSuccess && res.data?.data) {
                setRoles(res.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch roles", error);
        }
    }, [assignedSiteId, isVendorAdmin, vendorId]);

    const fetchStaffLogins = useCallback(async () => {
        if (!isVendorAdmin || !vendorId || isAllStaff) {
            setStaffLogins([]);
            return;
        }
        try {
            const response = await staffService.getStaffLogins(Number(vendorId));
            setStaffLogins(response.data?.isSuccess && Array.isArray(response.data?.data) ? response.data.data : []);
        } catch (error) {
            console.error("Failed to load staff logins", error);
            setStaffLogins([]);
        }
    }, [isVendorAdmin, vendorId, isAllStaff]);

    useEffect(() => {
        fetchStaff();
    }, [fetchStaff]);

    useEffect(() => {
        if (!isAllStaff) return undefined;
        const siteId = assignedSiteId;
        if (!siteId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const [unitRes, vendorRes] = await Promise.all([
                    apiClient.get(UNIT_LIST_URL, { params: { siteId, pageNumber: 1, pageSize: 1000 } }),
                    apiClient.get("/vendor-management/vendor/GetVendors", { params: { siteId, pageNumber: 1, pageSize: 500 } })
                ]);
                const rawUnits = unitRes.data?.data || unitRes.data?.Data || [];
                const unitList = (Array.isArray(rawUnits) ? rawUnits : []).map((unit) => ({
                    id: unit.Id || unit.id || unit.UnitId || unit.unitId,
                    siteId: unit.SiteId || unit.siteId,
                    name: unit.UnitName || unit.unitName || "Unit"
                })).filter((unit) => unit.id && (unit.siteId == null || Number(unit.siteId) === Number(siteId)));
                const rawVendors = vendorRes.data?.data || [];
                const vendorList = (Array.isArray(rawVendors) ? rawVendors : []).map((vendor) => ({
                    id: vendor.vendorId,
                    name: vendor.vendorName || "Vendor"
                })).filter((vendor) => vendor.id);
                if (!cancelled) {
                    setUnits(unitList);
                    setVendors(vendorList);
                }
            } catch (error) {
                console.error("Failed to fetch staff filters", error);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAllStaff, assignedSiteId]);

    useEffect(() => {
        if (isVendorAdmin) {
            fetchRoles();
            fetchStaffLogins();
        }
    }, [fetchRoles, fetchStaffLogins, isVendorAdmin]);

    const tableHeaders = [
        { label: "STAFF CODE", key: "generatedUniqueId", align: "center" },
        { label: "STAFF NAME", key: "fullName", align: "left" },
        { label: "PHONE", key: "mobileNumber", align: "center" },
        { label: "ROLE", key: "roleName", align: "left" },
        { label: "DUTY", key: "shift", align: "left" },
        { label: "DOCUMENT", key: "document", align: "center" },
        { label: "STATUS", key: "employmentStatus", align: "center" },
        ...(isVendorAdmin && !isAllStaff ? [{ label: "LOGIN", key: "login", align: "center" }] : []),
        { label: "ACTIONS", key: "actions", align: "center" }
    ];

    const renderTableCell = (key, row) => {
        if (key === "generatedUniqueId") {
            return (
                <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">
                    {row.generatedUniqueId || "—"}
                </span>
            );
        }

        if (key === "fullName") {
            return (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center shadow-sm shrink-0">
                        {row.photoUrl ? (
                            <img
                                src={row.photoUrl}
                                alt={row.fullName}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                }}
                            />
                        ) : (
                            <FontAwesomeIcon icon={faUser} className="text-gray-400 text-xs" />
                        )}
                    </div>
                    <div className="flex flex-col text-left">
                        <span className="text-[13px] font-semibold text-gray-800 dark:text-textprimary tracking-tight">{row.fullName}</span>
                        <span className="text-[10px] text-gray-400 dark:text-textsecondary font-normal">ID : {row.staffId}</span>
                    </div>
                </div>
            );
        }

        if (key === "mobileNumber") {
            return <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">{row.mobileNumber || "—"}</span>;
        }

        if (key === "roleName") {
            const parts = (row.roleName || "—").split(" ");
            return (
                <div className="flex flex-col text-left">
                    <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">{parts[0]}</span>
                    {parts.length > 1 && (
                        <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">{parts.slice(1).join(" ")}</span>
                    )}
                </div>
            );
        }

        if (key === "shift") {
            const startDate = row.allotment?.startDate ? new Date(row.allotment.startDate).toLocaleDateString('en-GB') : "";
            const endDate = row.allotment?.endDate ? new Date(row.allotment.endDate).toLocaleDateString('en-GB') : "";
            const dateStr = startDate && endDate ? `${startDate} to ${endDate}` : "";

            return (
                <div className="flex flex-col text-left">
                    <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">
                        {row.allotment?.unitNumber ? `${row.allotment.unitNumber} - ${row.allotment.unitName || ''}` : "—"}
                    </span>
                    {dateStr && (
                        <span className="text-[10px] text-gray-400 dark:text-textsecondary font-normal mt-0.5">
                            {dateStr}
                        </span>
                    )}
                </div>
            );
        }

        if (key === "document") {
            if (row.document?.documentUrl) {
                return (
                    <button
                        type="button"
                        onClick={() => setPreviewDoc({
                            isOpen: true,
                            doc: row.document,
                            staffName: row.fullName
                        })}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#EFF6FF] hover:bg-blue-100 dark:bg-blue-950/40 dark:hover:bg-blue-900/50 border border-[#DBEAFE] dark:border-blue-900/50 text-[#2563EB] dark:text-blue-400 rounded-xl text-xs font-semibold shadow-2xs transition-all duration-150 cursor-pointer"
                        title="Click to preview document"
                    >
                        <FontAwesomeIcon icon={faFileAlt} className="text-[#2563EB] dark:text-blue-400 text-xs" />
                        <span>{row.document.documentType || "Document"}</span>
                        <FontAwesomeIcon icon={faEye} className="text-[#3B82F6] dark:text-blue-400 text-[11px] ml-0.5" />
                    </button>
                );
            }
            return <span className="text-[13px] font-normal text-gray-400 dark:text-textsecondary">—</span>;
        }

        if (key === "login") {
            const personActive = (row.employmentStatus || "").trim().toLowerCase() === "active" || row.isActive === true || row.is_active === true;
            if (!personActive) {
                return <span className="text-[13px] text-gray-400">—</span>;
            }
            const login = staffLogins.find((item) => Number(item.staffId) === Number(row.staffId));
            if (!login) {
                return (
                    <button
                        type="button"
                        onClick={() => setLoginModal({
                            isOpen: true,
                            staff: row,
                            staffRoleId: row.staffRoleId ? String(row.staffRoleId) : (roles[0] ? String(roles[0].staffRoleId) : ""),
                            password: ""
                        })}
                        className="px-2 py-1 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700"
                    >
                        Create login
                    </button>
                );
            }
            return (
                <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-lg ${login.isActive ? "bg-green-50 text-green-700" : "bg-orange-50 text-orange-700"}`}>
                        {login.isActive ? "Login active" : "Login disabled"}
                    </span>
                    <button
                        type="button"
                        onClick={() => setLoginModal({
                            isOpen: true,
                            staff: row,
                            staffRoleId: login.staffRoleId ? String(login.staffRoleId) : (row.staffRoleId ? String(row.staffRoleId) : ""),
                            password: ""
                        })}
                        className="px-2 py-1 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700"
                    >
                        Set password
                    </button>
                </div>
            );
        }

        if (key === "employmentStatus") {
            const isActive = (row.employmentStatus || "").toLowerCase() === "active" || row.isActive;
            return (
                <span className={`px-3 py-1 text-xs font-bold rounded-lg ${isActive
                    ? "bg-[#DCFCE7] text-[#166534]"
                    : "bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-300"
                    }`}>
                    {isActive ? "Active" : "Inactive"}
                </span>
            );
        }

        return <span className="text-[13px] font-normal text-[#64748B] dark:text-textsecondary">{row[key] || "—"}</span>;
    };

    const onPageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, pageNumber: newPage }));
    };

    const handleLimitChange = (newLimit) => {
        setPagination((prev) => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
    };

    const handleCreateLogin = async () => {
        const password = (loginModal.password || "").trim();
        if (!loginModal.staff || !loginModal.staffRoleId) {
            toast.error("Select a role");
            return;
        }
        if (!password) {
            toast.error("Enter a password");
            return;
        }
        try {
            const response = await staffService.createStaffLogin({
                staffId: loginModal.staff.staffId,
                vendorId: Number(vendorId),
                staffRoleId: Number(loginModal.staffRoleId),
                password,
                createdBy: actorName
            });
            if (response.data?.isSuccess === false) {
                toast.error(response.data?.message || "Failed to create login");
                return;
            }
            setLoginModal({ isOpen: false, staff: null, staffRoleId: "", password: "" });
            setCredentialModal({
                isOpen: true,
                userName: response.data?.userName || loginModal.staff.mobileNumber || "",
                temporaryPassword: response.data?.temporaryPassword || password
            });
            fetchStaffLogins();
        } catch (error) {
            console.error("Failed to create login", error);
            const apiMessage = error.response?.data?.errors?.password?.[0] || error.response?.data?.message || "Failed to create login";
            toast.error(apiMessage);
        }
    };

    const handleDeleteStaff = async () => {
        if (!deleteModal.staffId) return;
        try {
            const modifiedBy = user?.name || user?.Name || user?.userName || "SiteAdmin";
            const response = await staffService.deleteStaff(deleteModal.staffId, modifiedBy);
            if (response.data?.isSuccess === false) {
                toast.error(response.data?.message || "Failed to delete staff");
                return;
            }
            toast.success(`Staff member deleted successfully`);
            setDeleteModal({ isOpen: false, staffId: null, staffName: "" });
            fetchStaff();
        } catch (error) {
            console.error("Failed to delete staff:", error);
            toast.error(error.response?.data?.message || "Failed to delete staff");
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs items={isAllStaff ? ["Workforce Management", "All Staff"] : ["Workforce Management", "Staff"]} />

            <PageHeadingSection
                Navbar={true}
                mainHeading={isAllStaff ? "All Staff" : "Staff List"}
                subHeading={isAllStaff
                    ? "Filter this site's staff by unit and vendor."
                    : "Manage individual staff members (drivers, maids, etc.) and control their daily access."}
                primaryBtnText="Add Staff"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => navigate("/zentcomm/workforce-management/staff/add")}
            />

            <div className="shrink-0 flex-1 flex flex-col mt-6">
                {isAllStaff && (
                    <div className="flex flex-wrap justify-end gap-3 mb-4">
                        <select
                            className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-cardbg text-gray-700 dark:text-textprimary text-xs rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-blue-500 w-[220px]"
                            value={filterUnitId}
                            onChange={(e) => {
                                setFilterUnitId(e.target.value);
                                setPagination(prev => ({ ...prev, pageNumber: 1 }));
                            }}
                        >
                            <option value="">All units</option>
                            {units.map((unit) => (
                                <option key={unit.id} value={unit.id}>{unit.name}</option>
                            ))}
                        </select>
                        <select
                            className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-cardbg text-gray-700 dark:text-textprimary text-xs rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-blue-500 w-[220px]"
                            value={filterVendorId}
                            onChange={(e) => {
                                setFilterVendorId(e.target.value);
                                setPagination(prev => ({ ...prev, pageNumber: 1 }));
                            }}
                        >
                            <option value="">All vendors</option>
                            {vendors.map((vendor) => (
                                <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                            ))}
                        </select>
                    </div>
                )}
                {isVendorAdmin && !isAllStaff && (
                    <div className="flex justify-end mb-4">
                        <select
                            className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-cardbg text-gray-700 dark:text-textprimary text-xs rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-blue-500 w-[220px]"
                            value={selectedRole}
                            onChange={(e) => {
                                setSelectedRole(e.target.value);
                                setPagination(prev => ({ ...prev, pageNumber: 1 }));
                            }}
                        >
                            <option value="">All Roles</option>
                            {roles.map((r) => (
                                <option key={r.staffRoleId} value={r.staffRoleId}>
                                    {r.roleName}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <CommonTable
                    loading={loading}
                    tableHeading="Staff List"
                    headers={tableHeaders}
                    data={staffList}
                    renderCell={renderTableCell}
                    pagination={{
                        pageNumber: pagination.pageNumber,
                        pageSize: pagination.pageSize,
                        totalRecords: pagination.totalRecords,
                        totalPages: Math.ceil(pagination.totalRecords / pagination.pageSize) || 1
                    }}
                    customAction={{
                        label: "Manage Staff",
                        icon: faEye,
                        onClick: (row) => navigate("/zentcomm/workforce-management/staff/add", { state: { staffData: row } })
                    }}
                    onDelete={(row) => setDeleteModal({ isOpen: true, staffId: row.staffId || row.id, staffName: row.fullName })}
                    onPageChange={onPageChange}
                    onLimitChange={handleLimitChange}
                    isLoading={loading}
                />
            </div>

            {/* Document Preview Modal */}
            <CommonModal
                isOpen={previewDoc.isOpen}
                onClose={() => setPreviewDoc({ isOpen: false, doc: null, staffName: "" })}
                title={`Document Preview - ${previewDoc.doc?.documentType || "Identity Document"}`}
                subTitle={previewDoc.staffName ? `Staff Member: ${previewDoc.staffName}` : "Uploaded Document Preview"}
                footerContent={
                    <div className="flex items-center justify-between w-full">
                        <div className="text-xs text-[#64748B] dark:text-textsecondary">
                            Document Type: <span className="font-semibold text-gray-800 dark:text-textprimary">{previewDoc.doc?.documentType || "—"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {previewDoc.doc?.documentUrl && (
                                <a
                                    href={previewDoc.doc.documentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[11px]" />
                                    Open Original
                                </a>
                            )}
                            <button
                                type="button"
                                onClick={() => setPreviewDoc({ isOpen: false, doc: null, staffName: "" })}
                                className="px-4 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-cardbg dark:hover:bg-actionitembg text-gray-700 dark:text-textprimary rounded-lg text-xs font-semibold transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                }
            >
                <div className="flex flex-col gap-4">
                    {/* Document Meta Information Header */}
                    <div className="grid grid-cols-2 gap-3 p-3.5 bg-gray-50 dark:bg-dashboardbg rounded-lg border border-gray-200/80 dark:border-bordercolor">
                        <div>
                            <span className="block text-[11px] text-gray-500 dark:text-textsecondary uppercase tracking-wider font-medium">Document Type</span>
                            <span className="text-xs font-semibold text-gray-800 dark:text-textprimary mt-0.5 block">
                                {previewDoc.doc?.documentType || "—"}
                            </span>
                        </div>
                        <div>
                            <span className="block text-[11px] text-gray-500 dark:text-textsecondary uppercase tracking-wider font-medium">Document Number</span>
                            <span className="text-xs font-semibold text-gray-800 dark:text-textprimary mt-0.5 block">
                                {previewDoc.doc?.documentNumber || "—"}
                            </span>
                        </div>
                    </div>

                    {/* Document Display / Preview */}
                    <div className="flex items-center justify-center p-2 bg-gray-100/60 dark:bg-cardbg rounded-lg min-h-[250px] max-h-[60vh] overflow-auto border border-gray-200 dark:border-bordercolor">
                        {previewDoc.doc?.documentUrl ? (
                            previewDoc.doc.documentUrl.toLowerCase().endsWith(".pdf") ? (
                                <iframe
                                    src={previewDoc.doc.documentUrl}
                                    title="Document Preview PDF"
                                    className="w-full h-[50vh] rounded border-0"
                                />
                            ) : (
                                <img
                                    src={previewDoc.doc.documentUrl}
                                    alt={previewDoc.doc?.documentType || "Document"}
                                    className="max-h-[50vh] max-w-full object-contain rounded-md shadow-sm"
                                    onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        e.currentTarget.nextSibling.style.display = "flex";
                                    }}
                                />
                            )
                        ) : (
                            <div className="text-center py-10 text-gray-400 text-xs">
                                No document file available for preview
                            </div>
                        )}
                        <div className="hidden flex-col items-center justify-center py-10 text-gray-400 text-xs gap-2">
                            <span>Could not load document preview inline.</span>
                            {previewDoc.doc?.documentUrl && (
                                <a
                                    href={previewDoc.doc.documentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline font-medium"
                                >
                                    Click here to open directly
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </CommonModal>

            <CommonModal
                isOpen={loginModal.isOpen}
                onClose={() => setLoginModal({ isOpen: false, staff: null, staffRoleId: "", password: "" })}
                title="Create login"
                subTitle={loginModal.staff?.fullName || ""}
                footerContent={
                    <div className="flex justify-end gap-2 w-full">
                        <button type="button" onClick={() => setLoginModal({ isOpen: false, staff: null, staffRoleId: "", password: "" })} className="px-3 py-1.5 text-xs rounded-lg bg-gray-100">Cancel</button>
                        <button type="button" onClick={handleCreateLogin} className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white">Create</button>
                    </div>
                }
            >
                <label className="flex flex-col gap-1 text-sm">
                    <span>Role</span>
                    <select
                        className="border border-gray-200 rounded-lg px-3 py-2"
                        value={loginModal.staffRoleId}
                        onChange={(e) => setLoginModal((prev) => ({ ...prev, staffRoleId: e.target.value }))}
                    >
                        <option value="">Select a role</option>
                        {roles.map((role) => (
                            <option key={role.staffRoleId} value={role.staffRoleId}>{role.roleName}</option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-sm mt-3">
                    <span>Password</span>
                    <input
                        type="password"
                        className="border border-gray-200 rounded-lg px-3 py-2"
                        value={loginModal.password}
                        onChange={(e) => setLoginModal((prev) => ({ ...prev, password: e.target.value }))}
                        placeholder="Enter password"
                        autoComplete="new-password"
                    />
                    <span className="text-xs text-gray-500">Sign in with the staff mobile number and this password.</span>
                </label>
            </CommonModal>

            <CommonModal
                isOpen={credentialModal.isOpen}
                onClose={() => setCredentialModal({ isOpen: false, userName: "", temporaryPassword: "" })}
                title="Staff login ready"
                subTitle="Sign in with this mobile number and password."
            >
                <div className="flex flex-col gap-2 text-sm">
                    <div>Mobile: <span className="font-semibold">{credentialModal.userName}</span></div>
                    <div>Password: <span className="font-semibold">{credentialModal.temporaryPassword}</span></div>
                </div>
            </CommonModal>

            <DeleteModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, staffId: null, staffName: "" })}
                onConfirm={handleDeleteStaff}
                title="Delete Staff"
                message={`Are you sure you want to delete "${deleteModal.staffName}"? This action cannot be undone.`}
            />
        </div>
    );
};

export default StaffList;

